import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Heart,
  Home,
  KeyRound,
  LogOut,
  MapPin,
  MessageSquareText,
  PackageCheck,
  Pencil,
  Send,
  ShieldCheck,
  Star,
  Trash2,
  Undo2,
  UserRound,
  X,
} from "lucide-react";
import { api } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import { resolveProductImage } from "../utils/productAssets";
import { formatUsDate } from "../utils/timezone";

const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#3991D5] focus:ring-4 focus:ring-[#70C6FE]/20";
const tabs = [
  { id: "orders", label: "Orders", icon: PackageCheck },
  { id: "wishlist", label: "Wishlist", icon: Heart },
  { id: "addresses", label: "Addresses", icon: MapPin },
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "security", label: "Security", icon: ShieldCheck },
];

const emptyAddress = {
  label: "Default",
  fullName: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
  isDefault: true,
};
const REVIEW_SNOOZE_KEY = "bobbinusa_review_snoozes";
const REVIEW_SNOOZE_MS = 24 * 1000;
const RETURN_WINDOW_DAYS = 14;

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function statusClass(status) {
  if (["paid", "processing", "shipped", "delivered"].includes(status)) return "bg-emerald-50 text-emerald-700";
  if (["cancelled", "returned", "failed"].includes(status)) return "bg-red-50 text-red-700";
  return "bg-amber-50 text-amber-700";
}

function reviewProductId(item) {
  return String(item?.productId || item?.sku || item?.name || "");
}

function reviewItemKey(order, item) {
  return `${order?._id || order?.id}:${reviewProductId(item)}`;
}

function returnItemKey(order, item) {
  return `${order?._id || order?.id}:${item?.sku || item?.productId || item?.name}`;
}

function getDeliveryDate(order) {
  if (order?.deliveredAt) return new Date(order.deliveredAt);
  if (order?.status === "delivered") return new Date(order.updatedAt || order.createdAt);
  return null;
}

function getReturnEligibility(order) {
  const deliveredAt = getDeliveryDate(order);
  if (!deliveredAt || Number.isNaN(deliveredAt.getTime())) return { eligible: false, label: "Delivery date unavailable" };
  const eligibleUntil = new Date(deliveredAt);
  eligibleUntil.setDate(eligibleUntil.getDate() + RETURN_WINDOW_DAYS);
  const eligible = Date.now() <= eligibleUntil.getTime();
  return {
    eligible,
    eligibleUntil,
    label: eligible ? `Return by ${formatUsDate(eligibleUntil)}` : "Return window closed",
  };
}

function readReviewSnoozes() {
  try {
    return JSON.parse(localStorage.getItem(REVIEW_SNOOZE_KEY) || "{}");
  } catch {
    return {};
  }
}

function Toast({ message, type }) {
  if (!message) return null;
  const classes = type === "error"
    ? "border-red-200 bg-red-50 text-red-800"
    : "border-emerald-200 bg-emerald-50 text-emerald-800";
  return <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-semibold ${classes}`}>{message}</div>;
}

function AccountCard({ children, icon: Icon, title, subtitle }) {
  return (
    <section className="rounded-2xl border border-[#3991D5]/10 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-start gap-3">
        <span className="rounded-xl bg-blue-50 p-2 text-[#1278b5]">{Icon && <Icon className="h-5 w-5" />}</span>
        <div>
          <h2 className="font-gopher text-xl font-black uppercase text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm font-semibold text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function OrderTracking({ order }) {
  const steps = [
    ["pending", "Order received"],
    ["processing", "Processing"],
    ["shipped", "Shipped"],
    ["delivered", "Delivered"],
  ];
  const activeIndex = Math.max(0, steps.findIndex(([status]) => status === order.status));

  return (
    <div className="mt-4 grid gap-2 sm:grid-cols-4">
      {steps.map(([status, label], index) => (
        <div key={status} className={`rounded-xl px-3 py-2 text-xs font-black ${index <= activeIndex ? "bg-blue-50 text-[#1278b5]" : "bg-slate-50 text-slate-400"}`}>
          {label}
        </div>
      ))}
    </div>
  );
}

export default function UserAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReviewOrderId = searchParams.get("review") || "";
  const { logout, setUser, user } = useAuth();
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [returns, setReturns] = useState([]);
  const [reviewForms, setReviewForms] = useState({});
  const [returnForms, setReturnForms] = useState({});
  const [reviewSnoozes, setReviewSnoozes] = useState(readReviewSnoozes);
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [editingAddressId, setEditingAddressId] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const defaultAddress = useMemo(() => addresses.find((address) => address.isDefault) || addresses[0], [addresses]);
  const openOrders = orders.filter((order) => !["delivered", "cancelled", "returned"].includes(order.status)).length;
  const pendingReviewItems = useMemo(() => {
    const now = Date.now();
    return orders.flatMap((order) => {
      if (order.status !== "delivered") return [];
      return (order.items || []).map((item) => ({ order, item }));
    }).filter(({ order, item }) => {
      const productId = reviewProductId(item);
      const key = reviewItemKey(order, item);
      if (!productId) return false;
      if (reviewSnoozes[key] && Number(reviewSnoozes[key]) > now) return false;
      return !reviews.some((review) => {
        const reviewedOrderId = String(review.order?._id || review.order || "");
        return reviewedOrderId === String(order._id) && String(review.productId) === productId;
      });
    }).sort((left, right) => Number(String(right.order._id) === requestedReviewOrderId) - Number(String(left.order._id) === requestedReviewOrderId));
  }, [orders, requestedReviewOrderId, reviewSnoozes, reviews]);

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    window.setTimeout(() => setMessage(""), 3500);
  };

  const refreshAccount = async () => {
    setLoading(true);
    try {
      const [orderData, wishlistData, meData, reviewData] = await Promise.all([
        api.get("/orders/my"),
        api.get("/wishlist"),
        api.get("/auth/me"),
        api.get("/reviews/my").catch(() => ({ reviews: [] })),
      ]);
      const returnData = await api.get("/returns/my").catch(() => ({ returns: [] }));
      setOrders(orderData.orders || []);
      setWishlist(wishlistData.items || []);
      setReviews(reviewData.reviews || []);
      setReturns(returnData.returns || []);
      setAddresses(meData.user?.addresses || []);
      setUser(meData.user);
      localStorage.setItem("authUser", JSON.stringify(meData.user));
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateProfile = async (event) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const data = await api.patch("/account/profile", Object.fromEntries(formData.entries()));
      setUser(data.user);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      showMessage("Profile updated.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const updatePassword = async (event) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const payload = Object.fromEntries(formData.entries());
      if (payload.newPassword !== payload.confirmPassword) {
        showMessage("New password and confirmation do not match.", "error");
        return;
      }
      await api.patch("/account/password", payload);
      event.currentTarget.reset();
      showMessage("Password updated.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const updateAddressForm = (event) => {
    const { checked, name, type, value } = event.target;
    setAddressForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const resetAddressForm = () => {
    setAddressForm(emptyAddress);
    setEditingAddressId("");
  };

  const editAddress = (address) => {
    setEditingAddressId(address._id);
    setAddressForm({
      label: address.label || "Default",
      fullName: address.fullName || "",
      phone: address.phone || "",
      line1: address.line1 || "",
      line2: address.line2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      country: address.country || "US",
      isDefault: Boolean(address.isDefault),
    });
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    try {
      const data = editingAddressId
        ? await api.patch(`/account/addresses/${editingAddressId}`, addressForm)
        : await api.post("/account/addresses", addressForm);
      setAddresses(data.addresses || []);
      setUser((current) => ({ ...current, addresses: data.addresses || [] }));
      localStorage.setItem("authUser", JSON.stringify({ ...user, addresses: data.addresses || [] }));
      resetAddressForm();
      showMessage(editingAddressId ? "Address updated." : "Address added.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const deleteAddress = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const data = await api.delete(`/account/addresses/${addressId}`);
      setAddresses(data.addresses || []);
      setUser((current) => ({ ...current, addresses: data.addresses || [] }));
      showMessage("Address deleted.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const removeWishlistItem = async (itemId) => {
    try {
      await api.delete(`/wishlist/${itemId}`);
      setWishlist((current) => current.filter((item) => item._id !== itemId));
      showMessage("Removed from wishlist.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const updateReviewForm = (key, patch) => {
    setReviewForms((current) => ({
      ...current,
      [key]: { rating: 0, comment: "", ...(current[key] || {}), ...patch },
    }));
  };

  const snoozeReviewPrompt = (key) => {
    const next = { ...reviewSnoozes, [key]: Date.now() + REVIEW_SNOOZE_MS };
    setReviewSnoozes(next);
    localStorage.setItem(REVIEW_SNOOZE_KEY, JSON.stringify(next));
  };

  const submitProductReview = async (order, item) => {
    const key = reviewItemKey(order, item);
    const form = reviewForms[key] || {};
    if (!form.rating) {
      showMessage("Choose a rating before submitting your review.", "error");
      return;
    }
    if (!form.comment?.trim()) {
      showMessage("Write a short review before submitting.", "error");
      return;
    }
    try {
      const data = await api.post("/reviews", {
        orderId: order._id,
        productId: reviewProductId(item),
        rating: Number(form.rating),
        comment: form.comment.trim(),
      });
      setReviews((current) => [data.review, ...current.filter((review) => review._id !== data.review?._id)]);
      setReviewForms((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      showMessage("Review submitted. Thank you.");
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const updateReturnForm = (key, patch) => {
    setReturnForms((current) => ({
      ...current,
      [key]: {
        reason: "no_longer_needed",
        message: "",
        originalPackagingConfirmed: false,
        policyAccepted: false,
        ...(current[key] || {}),
        ...patch,
      },
    }));
  };

  const submitReturnRequest = async (order, item) => {
    const key = returnItemKey(order, item);
    const form = returnForms[key] || {};
    if (!form.policyAccepted || !form.originalPackagingConfirmed) {
      showMessage("Confirm the return policy and original package condition before submitting.", "error");
      return;
    }

    try {
      const data = await api.post("/returns/customer", {
        orderId: order._id,
        itemSku: item.sku || item.productId,
        reason: form.reason || "no_longer_needed",
        message: form.message || "",
        originalPackagingConfirmed: Boolean(form.originalPackagingConfirmed),
        policyAccepted: Boolean(form.policyAccepted),
      });
      setReturns((current) => [data.returnRequest, ...current]);
      showMessage(`Return request submitted: ${data.returnRequest.rmaNumber}`);
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/account");
  };

  return (
    <section className="bg-[#f7fbff] px-4 py-10 text-slate-900 sm:px-6 lg:py-14">
      <div className="container">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#1278b5]">Customer account</p>
            <h1 className="mt-2 font-gopher text-4xl font-black uppercase">My account</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">Manage orders, tracking, saved products, addresses, and security.</p>
          </div>
          <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white" onClick={handleLogout} type="button">
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>

        <Toast message={message} type={messageType} />

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <p className="text-sm font-bold text-slate-500">Open orders</p>
            <p className="mt-2 text-3xl font-black text-[#3991D5]">{openOrders}</p>
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <p className="text-sm font-bold text-slate-500">Wishlist</p>
            <p className="mt-2 text-3xl font-black text-[#3991D5]">{wishlist.length}</p>
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <p className="text-sm font-bold text-slate-500">Addresses</p>
            <p className="mt-2 text-3xl font-black text-[#3991D5]">{addresses.length}</p>
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <p className="text-sm font-bold text-slate-500">Default ship to</p>
            <p className="mt-2 truncate text-sm font-black text-slate-900">{defaultAddress?.city || "Not set"}</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <aside className="h-fit rounded-2xl border border-[#3991D5]/10 bg-white p-3 shadow-sm">
            {tabs.map(({ icon: Icon, id, label }) => (
              <button
                className={`mb-1 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-black ${activeTab === id ? "bg-[#3991D5] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                key={id}
                onClick={() => setActiveTab(id)}
                type="button"
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </aside>

          <main className="min-w-0">
            {loading ? (
              <div className="rounded-2xl bg-white p-8 text-sm font-bold text-slate-500 shadow-sm">Loading account...</div>
            ) : (
              <>
                {activeTab === "orders" && (
                  <AccountCard icon={PackageCheck} title="Orders" subtitle="Track website, phone, and invoice orders tied to this account.">
                    <div className="space-y-4">
                      {pendingReviewItems.length > 0 && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                          <div className="mb-3 flex items-start gap-3">
                            <span className="rounded-xl bg-white p-2 text-amber-600">
                              <MessageSquareText className="h-5 w-5" />
                            </span>
                            <div>
                              <p className="font-black text-slate-900">Delivered items are ready for review</p>
                              <p className="mt-1 text-sm font-semibold text-slate-600">Close a reminder to hide it for 24 hours. It returns until the item is reviewed.</p>
                            </div>
                          </div>
                          <div className="grid gap-3">
                            {pendingReviewItems.slice(0, 3).map(({ order, item }) => {
                              const key = reviewItemKey(order, item);
                              const form = reviewForms[key] || {};
                              return (
                                <div className={`rounded-xl bg-white p-4 shadow-sm ${String(order._id) === requestedReviewOrderId ? "ring-2 ring-[#3991D5]" : ""}`} key={key}>
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-black text-slate-900">{item.name}</p>
                                      <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-400">{order.orderNumber} {item.sku ? `/ SKU ${item.sku}` : ""}</p>
                                    </div>
                                    <button className="rounded-full p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700" onClick={() => snoozeReviewPrompt(key)} type="button" aria-label="Close review reminder">
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <div className="mt-3 flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((rating) => (
                                      <button className="text-amber-500" key={rating} onClick={() => updateReviewForm(key, { rating })} type="button" aria-label={`${rating} star rating`}>
                                        <Star className="h-5 w-5" fill={Number(form.rating || 0) >= rating ? "currentColor" : "none"} />
                                      </button>
                                    ))}
                                  </div>
                                  <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto]">
                                    <textarea
                                      className="min-h-[86px] rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#3991D5] focus:ring-4 focus:ring-[#70C6FE]/20"
                                      onChange={(event) => updateReviewForm(key, { comment: event.target.value })}
                                      placeholder="Share product quality, fit, or part accuracy."
                                      value={form.comment || ""}
                                    />
                                    <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#3991D5] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" onClick={() => submitProductReview(order, item)} type="button">
                                      <Send className="h-4 w-4" />
                                      Submit
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {orders.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-600">No orders yet.</p>}
                      {orders.map((order) => (
                        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={order._id}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-lg font-black text-slate-900">{order.orderNumber}</p>
                              <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-400">{formatUsDate(order.createdAt)} · {order.channel}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-black text-[#3991D5]">{money(order.total)}</p>
                              <span className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-black uppercase ${statusClass(order.status)}`}>{order.status}</span>
                            </div>
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <div className="rounded-xl bg-white p-3 text-sm">
                              <p className="font-black text-slate-900">Items</p>
                              <p className="mt-1 text-slate-600">{order.items?.map((item) => `${item.quantity}x ${item.name}`).join(", ")}</p>
                            </div>
                            <div className="rounded-xl bg-white p-3 text-sm">
                              <p className="font-black text-slate-900">Tracking</p>
                              <p className="mt-1 text-slate-600">{order.trackingNumber ? `${order.carrier} ${order.trackingNumber}` : "Tracking not assigned yet."}</p>
                            </div>
                          </div>
                          <OrderTracking order={order} />
                          {order.status === "delivered" && (
                            <div className="mt-4 rounded-2xl border border-blue-100 bg-white p-4">
                              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <p className="flex items-center gap-2 text-sm font-black text-slate-900">
                                    <Undo2 className="h-4 w-4 text-[#1278b5]" />
                                    Request a return
                                  </p>
                                  <p className="mt-1 text-xs font-bold text-slate-500">
                                    Returns are accepted within 14 days from delivery and only in original package, no exception.
                                  </p>
                                </div>
                                <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-black uppercase text-[#1278b5]">
                                  {getReturnEligibility(order).label}
                                </span>
                              </div>
                              <div className="grid gap-3">
                                {(order.items || []).map((item) => {
                                  const key = returnItemKey(order, item);
                                  const form = returnForms[key] || {};
                                  const existingReturn = returns.find((returnRequest) =>
                                    String(returnRequest.order?._id || returnRequest.order) === String(order._id) &&
                                    String(returnRequest.sku || "") === String(item.sku || item.productId || "")
                                  );
                                  const eligibility = getReturnEligibility(order);
                                  return (
                                    <div className="rounded-xl bg-slate-50 p-3" key={key}>
                                      <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-black text-slate-900">{item.name}</p>
                                          <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-400">SKU {item.sku || "N/A"} / Qty {item.quantity}</p>
                                        </div>
                                        {existingReturn && <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase text-emerald-700">{existingReturn.rmaNumber}</span>}
                                      </div>
                                      {!existingReturn && eligibility.eligible && (
                                        <div className="mt-3 grid gap-3">
                                          <select
                                            className={inputClass}
                                            onChange={(event) => updateReturnForm(key, { reason: event.target.value })}
                                            value={form.reason || "no_longer_needed"}
                                          >
                                            <option value="no_longer_needed">It was not for me</option>
                                            <option value="wrong_item">Wrong item</option>
                                            <option value="damaged">Damaged</option>
                                            <option value="defective">Defective</option>
                                            <option value="exchange">Exchange</option>
                                            <option value="other">Other</option>
                                          </select>
                                          <textarea
                                            className="min-h-[76px] rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#3991D5] focus:ring-4 focus:ring-[#70C6FE]/20"
                                            onChange={(event) => updateReturnForm(key, { message: event.target.value })}
                                            placeholder="Optional note for our returns team"
                                            value={form.message || ""}
                                          />
                                          <label className="flex items-start gap-2 text-xs font-bold text-slate-600">
                                            <input checked={Boolean(form.originalPackagingConfirmed)} onChange={(event) => updateReturnForm(key, { originalPackagingConfirmed: event.target.checked })} type="checkbox" />
                                            Item is in original package and perfect condition.
                                          </label>
                                          <label className="flex items-start gap-2 text-xs font-bold text-slate-600">
                                            <input checked={Boolean(form.policyAccepted)} onChange={(event) => updateReturnForm(key, { policyAccepted: event.target.checked })} type="checkbox" />
                                            I understand refund is admin-approved after inspection and shipping cost is deducted.
                                          </label>
                                          <button className="rounded-xl bg-[#3991D5] px-4 py-3 text-sm font-black text-white" onClick={() => submitReturnRequest(order, item)} type="button">
                                            Submit Return Request
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccountCard>
                )}

                {activeTab === "wishlist" && (
                  <AccountCard icon={Heart} title="Wishlist" subtitle="Saved products for future orders.">
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {wishlist.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-600 md:col-span-2 xl:col-span-3">No saved products yet.</p>}
                      {wishlist.map((item) => (
                        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={item._id}>
                          <div className="flex h-32 items-center justify-center rounded-xl bg-white p-3">
                            <img alt={item.name} className="h-full w-full object-contain" src={resolveProductImage(item.image)} />
                          </div>
                          <p className="mt-3 text-sm font-black text-slate-900">{item.name}</p>
                          {item.sku && <p className="mt-1 text-xs font-bold text-slate-500">SKU: {item.sku}</p>}
                          <p className="mt-2 font-black text-[#3991D5]">{money(item.price)}</p>
                          <div className="mt-4 flex gap-2">
                            <Link className="flex-1 rounded-xl bg-[#3991D5] px-3 py-2 text-center text-xs font-black text-white" to={`/products/${item.sku || item.productId}`}>View</Link>
                            <button className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-red-600" onClick={() => removeWishlistItem(item._id)} type="button">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccountCard>
                )}

                {activeTab === "addresses" && (
                  <div className="grid gap-5 xl:grid-cols-[1fr_390px]">
                    <AccountCard icon={Home} title="Saved addresses" subtitle="Use defaults for checkout and phone orders.">
                      <div className="grid gap-3">
                        {addresses.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-600">No addresses saved yet.</p>}
                        {addresses.map((address) => (
                          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={address._id}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="font-black text-slate-900">{address.label || "Address"} {address.isDefault && <span className="ml-2 rounded-full bg-blue-50 px-2 py-1 text-[10px] uppercase text-[#1278b5]">Default</span>}</p>
                                <p className="mt-2 text-sm font-semibold text-slate-600">{address.fullName}</p>
                                <p className="text-sm text-slate-500">{[address.line1, address.line2, address.city, address.state, address.postalCode, address.country].filter(Boolean).join(", ")}</p>
                                <p className="mt-1 text-sm text-slate-500">{address.phone}</p>
                              </div>
                              <div className="flex gap-2">
                                <button className="rounded-xl bg-white p-2 text-slate-600" onClick={() => editAddress(address)} type="button"><Pencil className="h-4 w-4" /></button>
                                <button className="rounded-xl bg-red-50 p-2 text-red-600" onClick={() => deleteAddress(address._id)} type="button"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccountCard>

                    <AccountCard icon={MapPin} title={editingAddressId ? "Edit address" : "Add address"}>
                      <form className="space-y-3" onSubmit={saveAddress}>
                        {[
                          ["label", "Label"], ["fullName", "Full name"], ["phone", "Phone"], ["line1", "Address"], ["line2", "Address line 2"], ["city", "City"], ["state", "State"], ["postalCode", "Postal code"], ["country", "Country"],
                        ].map(([name, label]) => (
                          <input className={inputClass} key={name} name={name} onChange={updateAddressForm} placeholder={label} required={["fullName", "line1", "city", "state", "postalCode"].includes(name)} value={addressForm[name]} />
                        ))}
                        <label className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm font-bold text-[#1278b5]">
                          <input checked={addressForm.isDefault} name="isDefault" onChange={updateAddressForm} type="checkbox" /> Set as default
                        </label>
                        <div className="flex gap-2">
                          <button className="flex-1 rounded-xl bg-[#3991D5] px-5 py-3 text-sm font-bold text-white">{editingAddressId ? "Update" : "Save"} address</button>
                          {editingAddressId && <button className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-600" onClick={resetAddressForm} type="button">Cancel</button>}
                        </div>
                      </form>
                    </AccountCard>
                  </div>
                )}

                {activeTab === "profile" && (
                  <AccountCard icon={UserRound} title="Profile" subtitle="Update contact details used for orders and support.">
                    <form className="grid gap-4 md:grid-cols-2" onSubmit={updateProfile}>
                      <input className={inputClass} defaultValue={user?.name || ""} name="name" placeholder="Full name" required />
                      <input className={inputClass} value={user?.email || ""} disabled />
                      <input className={inputClass} defaultValue={user?.phone || ""} name="phone" placeholder="Phone" />
                      <button className="rounded-xl bg-[#3991D5] px-5 py-3 text-sm font-bold text-white md:col-span-2">Save profile</button>
                    </form>
                  </AccountCard>
                )}

                {activeTab === "security" && (
                  <AccountCard icon={KeyRound} title="Security" subtitle="Update your password. Use at least 8 characters.">
                    <form className="grid gap-4 md:grid-cols-2" onSubmit={updatePassword}>
                      <input className={inputClass} name="currentPassword" placeholder="Current password" required type="password" />
                      <input className={inputClass} name="newPassword" placeholder="New password" required type="password" />
                      <input className={`${inputClass} md:col-span-2`} name="confirmPassword" placeholder="Confirm new password" required type="password" />
                      <button className="rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white md:col-span-2">Update password</button>
                    </form>
                  </AccountCard>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </section>
  );
}
